import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import type { Op } from 'quill/core';
import React, { useLayoutEffect, useMemo } from 'react';
import HomeActivities from './HomeActivities';
import HomeActivitiesButton from './HomeActivitiesButton';
import * as styles from './HomeView.module.scss';
import JulyProductUpdate, {
  shouldShowProductUpdate,
} from './JulyProductUpdate';
import OrganizationSummary from './organizationSummary/OrganizationSummary';
import useViewedPostsColumn from './useViewedPostsColumn';
import PageLayout from '../app/layout/PageLayout';
import RightPanel, { useRightPanel } from '../app/layout/RightPanel';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated/gql';
import PostSectionHeader from '../post/PostSectionHeader';
import { UIActions } from '../ui/uiSlice';
import ReferralModal from '../user/ReferralModal';
import { isFeatureEnabled } from '../utils/features';
import { openPricingModal } from '../utils/url/pricingPath';

const ORGANIZATION_SUMMARY_QUERY = gql(/** @complexity 2 */ `
  query organizationSummary {
    session: currentSession {
      organization {
        id
        summary
      }
    }
  }
`);

type HomeModal = 'pricing' | 'referral';

interface HomeViewProps {
  openModal?: HomeModal;
}

const useModal = (modal?: HomeModal) => {
  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    if (modal === 'pricing') {
      openPricingModal(dispatch);
    } else if (modal === 'referral') {
      dispatch(UIActions.openModal(ReferralModal));
    } else {
      return undefined;
    }

    return () => {
      dispatch(UIActions.closeModal());
    };
  }, [dispatch, modal]);
};

const HomeView = ({ openModal }: HomeViewProps) => {
  const { data, loading } = useQuery(ORGANIZATION_SUMMARY_QUERY);
  const organization = data?.session?.organization;
  const summary = useMemo(() => {
    if (organization?.summary) {
      return JSON.parse(organization.summary) as Op[];
    }
    return null;
  }, [organization]);
  const main = useViewedPostsColumn();
  useModal(openModal);

  const { openRightPanel } = useRightPanel();

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <TwoColumnRow
        main={(isAsideShown) => (
          <div className={styles.main}>
            <OrganizationSummary
              summary={summary}
              onActivityOpen={
                isAsideShown
                  ? undefined
                  : () => {
                      openRightPanel(
                        <RightPanel.Inner
                          header={
                            <RightPanel.Header
                              title="Activity"
                              controls={
                                <HomeActivitiesButton popoverPlacement="bottom-start" />
                              }
                            />
                          }
                        >
                          <HomeActivities />
                        </RightPanel.Inner>,
                      );
                    }
              }
            />
            {loading && !summary ? undefined : (
              <>
                <PostSectionHeader controls={null}>
                  <h3>Viewed</h3>
                </PostSectionHeader>
                {main}
              </>
            )}
          </div>
        )}
        aside={
          <div className={styles.activities}>
            {shouldShowProductUpdate && (
              <>
                <h4 className={styles.asideHeader}>New in Slab</h4>
                <JulyProductUpdate />
              </>
            )}
            <div
              className={classNames(styles.asideHeader, {
                [styles.isHomeActivitiesV2]:
                  isFeatureEnabled('home_activity_v2'),
              })}
            >
              <h4>Activity</h4>
              <HomeActivitiesButton popoverPlacement="bottom-end" />
            </div>
            <div className={styles.asideActivitiesContainer}>
              <HomeActivities />
            </div>
          </div>
        }
      />
    </PageLayout.Content>
  );
};

export default HomeView;
